import React, { useCallback, useMemo } from 'react';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';
import { useExpandedItems } from '@client/hooks/use-expand-elements.hooks';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { useElementListMaxValues } from '@features/proposals/use-proposals.hooks';
import {
  useSelectedElementId,
  useSelectedVersionProductsAndStoreProducts,
} from '@client/store/ui';
import {
  useNavigateToElementListSelection,
  useProjectNavigation,
} from '@client/hooks/use-router.hooks';
import {
  formatProductRowQuantity,
  getProductListElementName,
  getProductListGroupDisplayName,
  getProductListItemLocationCategoryId,
} from '@features/products/product-list.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { Page } from '@client/store/ui/ui.model';
import { requestScrollToElement } from '@features/proposals/ElementListRow/element-scroll-request.store';
import {
  getHomogeneousClimateTag,
  getProductElementClimateTag,
} from '@shared/helpers/product-element.helpers';

import { IconProps } from '@ui/Icon';
import { IProductListItem } from '@shared/models/product.model';
import type { IProductListGroupView } from '@features/products/product-list.helpers';
import ElementListRow, {
  ELEMENT_LIST_ROW_HEIGHT,
} from '@features/proposals/ElementListRow/ElementListRow';
import { ProductElementClimateTag } from './ProductElementClimateTag';
import { useProductGroupProposalBars } from './use-product-group-proposal-bars.hooks';
type ProductGroupItemRowProps = {
  productGroup: IProductListGroupView;
  depth?: number;
  categoryIcon?: IconProps<'elementCategory'>['icon'];
  /** When true, overrides local expanded state (used while searching). */
  forceExpanded?: boolean;
};

const EXPANDED_PREFIX = 'product-group';

const ProductGroupItemRow: React.FC<ProductGroupItemRowProps> = ({
  productGroup,
  depth = 1,
  categoryIcon,
  forceExpanded,
}) => {
  const { maxCo2: co2ePerGfaMax, maxCost: costPerGfaMax } =
    useElementListMaxValues();
  const selectedElementId = useSelectedElementId();
  const navigateToListSelection = useNavigateToElementListSelection();
  const productLookup = useSelectedVersionProductsAndStoreProducts();
  const { is: isExpanded, set: setExpanded } = useExpandedItems({
    defaultPrefix: EXPANDED_PREFIX,
  });
  const expanded = forceExpanded ?? isExpanded(productGroup.id);
  const handleExpandedChange = useCallback(
    (v: boolean) => setExpanded(productGroup.id, v),
    [setExpanded, productGroup.id],
  );

  const resolvedCategoryIcon = categoryIcon;
  const name = getProductListGroupDisplayName(productGroup, productLookup);
  const hasChildren = productGroup.elements.length > 0;

  const totalQuantity = getConversionFactorValue(
    productGroup.results,
    productGroup.unit,
  );
  const { quantityPlaceholder, quantityDisplayNumber, displayUnit } =
    formatProductRowQuantity(totalQuantity, productGroup.unit);

  const itemIds = useMemo(
    () => productGroup.elements.map((i) => i.id),
    [productGroup.elements],
  );
  const proposals = useProductGroupProposalBars(itemIds);
  const climateTag = useMemo(
    () => getHomogeneousClimateTag(productGroup.elements, productLookup),
    [productGroup.elements, productLookup],
  );

  const handleSelect = useCallback(
    () => navigateToListSelection(productGroup.id, { page: Page.Products }),
    [navigateToListSelection, productGroup.id],
  );

  const selected = selectedElementId === productGroup.id;

  return (
    <>
      <HideOutsideViewport
        height={ELEMENT_LIST_ROW_HEIGHT}
        alwaysVisible={selected}
      >
        <ElementListRow
          variant="element"
          depth={depth}
          name={name}
          categoryIcon={resolvedCategoryIcon}
          quantityPlaceholder={quantityPlaceholder}
          quantityDisplayNumber={quantityDisplayNumber}
          unit={productGroup.unit}
          quantityDisplayUnit={displayUnit}
          quantityReadonly
          nameTrailingSlot={
            climateTag ? (
              <ProductElementClimateTag tag={climateTag} />
            ) : undefined
          }
          expandable={hasChildren && !forceExpanded}
          expanded={expanded}
          onExpandedChange={handleExpandedChange}
          selected={selected}
          onSelect={handleSelect}
          proposals={proposals}
          maxCo2={co2ePerGfaMax || 1}
          maxCost={costPerGfaMax || 1}
        />
      </HideOutsideViewport>
      {expanded &&
        productGroup.visibleElements.map((item) => (
          <ProductListItemRow key={item.id} item={item} depth={depth + 1} />
        ))}
    </>
  );
};

const ProductListItemRow: React.FC<{
  item: IProductListItem;
  depth: number;
}> = ({ item, depth }) => {
  const { maxCo2: co2ePerGfaMax, maxCost: costPerGfaMax } =
    useElementListMaxValues();
  const selectedElementId = useSelectedElementId();
  const navigateToListSelection = useNavigateToElementListSelection();
  const navigateTo = useProjectNavigation();
  const productLookup = useSelectedVersionProductsAndStoreProducts();

  const name = getProductListElementName(item, {
    productLookup,
  });
  const locationCategoryId = getProductListItemLocationCategoryId(item);
  const locationIcon = getElementIconCategoryName(locationCategoryId);
  const parentElement = item.path.at(-1);
  const climateTag = useMemo(
    () => getProductElementClimateTag(item, productLookup),
    [item, productLookup],
  );

  const totalQuantity = getConversionFactorValue(item.results, item.unit);
  const { quantityPlaceholder, quantityDisplayNumber, displayUnit } =
    formatProductRowQuantity(totalQuantity, item.unit);

  const itemIds = useMemo(() => [item.id], [item.id]);
  const proposals = useProductGroupProposalBars(itemIds);

  const handleKebabClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      const targetId = parentElement?.id ?? item.id;
      requestScrollToElement(targetId);
      void navigateTo(
        { page: Page.Elements, elementId: targetId },
        { searchParams: { search: null } },
      );
    },
    [navigateTo, parentElement?.id, item.id],
  );

  const handleSelect = useCallback(
    () => navigateToListSelection(item.id, { page: Page.Products }),
    [navigateToListSelection, item.id],
  );

  const selected = selectedElementId === item.id;

  return (
    <HideOutsideViewport
      height={ELEMENT_LIST_ROW_HEIGHT}
      alwaysVisible={selected}
    >
      <ElementListRow
        variant="element"
        depth={depth}
        name={name}
        categoryIcon={locationIcon}
        quantityPlaceholder={quantityPlaceholder}
        quantityDisplayNumber={quantityDisplayNumber}
        unit={item.unit}
        quantityDisplayUnit={displayUnit}
        quantityReadonly
        nameTrailingSlot={
          climateTag ? <ProductElementClimateTag tag={climateTag} /> : undefined
        }
        onKebabClick={handleKebabClick}
        kebabIcon="ArrowForward"
        kebabTooltip="Go to element"
        selected={selected}
        onSelect={handleSelect}
        proposals={proposals}
        maxCo2={co2ePerGfaMax || 1}
        maxCost={costPerGfaMax || 1}
      />
    </HideOutsideViewport>
  );
};

export default ProductGroupItemRow;
